import { useEffect, useState, type PointerEvent } from 'react'
import { api, type AdminStats } from './api'
import { easeInOutCubic, easeOutCubic, useProgress } from './motion'
import StatTile from './StatTile'
import ViewsDayChart from './ViewsDayChart'

const WHEEL_COLORS = [
  'var(--color-ink)',
  'var(--color-rose)',
  'var(--color-rose-soft)',
  'var(--color-copy)',
  'var(--color-stone)',
  'var(--color-muted)',
  '#b08968',
  '#8a8178',
]

function Bars({ rows, labelKey }: { rows: { label: string; views: number }[]; labelKey?: string }) {
  const max = Math.max(...rows.map((row) => row.views), 1)
  const progress = useProgress(700, easeOutCubic)
  return (
    <ul className="mt-4 divide-y divide-rule">
      {rows.map((row, index) => (
        <li key={`${labelKey ?? ''}${row.label}`} className="py-2.5">
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="truncate">{row.label}</span>
            <span className="shrink-0 text-muted tabular-nums">{Math.round(row.views * progress).toLocaleString()}</span>
          </div>
          <div className="mt-1.5 h-1.5 bg-sand">
            <div
              className="admin-grow-x h-full bg-rose"
              style={{ width: `${(row.views / max) * 100}%`, animationDelay: `${index * 60}ms` }}
            />
          </div>
        </li>
      ))}
      {rows.length === 0 ? <li className="py-4 text-sm text-muted">No data yet.</li> : null}
    </ul>
  )
}

/** How much of a slice has been drawn once the sweep has covered `sweep` of the dial. */
function visibleArc(slice: { pct: number; offset: number }, sweep: number) {
  return Math.min(slice.pct, Math.max(0, sweep * 100 - slice.offset))
}

function StatWheel({
  rows,
  large = false,
}: {
  rows: { label: string; views: number }[]
  large?: boolean
}) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)
  // Slices sweep in clockwise from 12 o'clock, like a clock hand filling the dial.
  const sweep = useProgress(1200, easeInOutCubic)
  const total = rows.reduce((sum, row) => sum + row.views, 0)
  if (!total) return <p className="mt-4 text-sm text-muted">No data yet.</p>

  const radius = 15.9155
  let offset = 0
  const slices = rows.map((row, index) => {
    const pct = (row.views / total) * 100
    const slice = { ...row, pct, offset, color: WHEEL_COLORS[index % WHEEL_COLORS.length] }
    offset += pct
    return slice
  })
  const hover = hoverIndex == null ? null : slices[hoverIndex]
  const active = hover ?? slices[0]
  const activePct = Math.round(active.pct * sweep)

  function indexAt(event: PointerEvent<HTMLDivElement>) {
    const rect = event.currentTarget.getBoundingClientRect()
    const dx = event.clientX - (rect.left + rect.width / 2)
    const dy = event.clientY - (rect.top + rect.height / 2)
    if (dx === 0 && dy === 0) return hoverIndex
    const turn = (Math.atan2(dx, -dy) / (Math.PI * 2) + 1) % 1
    const pct = turn * 100
    const index = slices.findIndex((slice) => pct >= slice.offset && pct < slice.offset + slice.pct)
    return index === -1 ? slices.length - 1 : index
  }

  return (
    <div className={`mt-5 flex items-center pt-6 ${large ? 'flex-wrap gap-8' : 'gap-5'}`}>
      <div
        className={`relative shrink-0 cursor-crosshair overflow-visible ${large ? 'size-[168px]' : 'size-[140px]'}`}
        role="img"
        aria-label={slices.map((slice) => `${slice.label} ${Math.round(slice.pct)} percent`).join(', ')}
        onPointerMove={(event) => setHoverIndex(indexAt(event))}
        onPointerLeave={() => setHoverIndex(null)}
      >
        {hover ? (
          <div
            role="status"
            className="pointer-events-none absolute left-1/2 top-0 z-10 -translate-x-1/2 -translate-y-full whitespace-nowrap pb-1.5 text-center text-[11px] tracking-[0.08em] uppercase text-ink"
          >
            {hover.label} · {hover.views.toLocaleString()}
          </div>
        ) : null}
        <svg viewBox="0 0 36 36" className="size-full -rotate-90" aria-hidden="true">
          <circle cx="18" cy="18" r={radius} fill="none" className="stroke-sand" strokeWidth="3.6" />
          {slices.map((slice, index) => (
            <circle
              key={`${slice.label}-${index}`}
              cx="18"
              cy="18"
              r={radius}
              fill="none"
              stroke={slice.color}
              className="transition-[stroke-width,opacity] duration-200"
              style={{
                strokeWidth: hoverIndex === index ? 4.4 : 3.6,
                opacity: hoverIndex == null || hoverIndex === index ? 1 : 0.45,
              }}
              strokeDasharray={`${visibleArc(slice, sweep)} ${100 - visibleArc(slice, sweep)}`}
              strokeDashoffset={-slice.offset}
            />
          ))}
        </svg>
        <div className="pointer-events-none absolute inset-0 grid place-items-center">
          <div className="text-center">
            <p className={`font-serif leading-none ${large ? 'text-[30px]' : 'text-[26px]'}`}>{activePct}%</p>
            <p className={`mt-1 max-w-[7rem] truncate text-caps text-muted ${large ? 'text-[9px]' : 'text-[9px]'}`}>
              {active.label}
            </p>
          </div>
        </div>
      </div>
      <ul className={`min-w-0 text-sm ${large ? 'space-y-3' : 'space-y-2.5'}`}>
        {slices.map((slice, index) => (
          <li
            key={`${slice.label}-${index}`}
            className={`flex items-center gap-3 ${hoverIndex === index ? 'text-ink' : ''}`}
          >
            <span className="size-2.5 shrink-0" style={{ background: slice.color }} aria-hidden="true" />
            <span className="min-w-0">
              <span className="block truncate">{slice.label}</span>
              <span className="text-muted">
                {Math.round(slice.pct)}% · {slice.views.toLocaleString()}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function DeviceWheel({ devices }: { devices: { device: string; views: number }[] }) {
  const desktop = devices.filter((row) => row.device === 'desktop').reduce((sum, row) => sum + row.views, 0)
  const mobile = devices.filter((row) => row.device !== 'desktop').reduce((sum, row) => sum + row.views, 0)
  return (
    <StatWheel
      large
      rows={[
        { label: 'Desktop', views: desktop },
        { label: 'Mobile', views: mobile },
      ]}
    />
  )
}

export default function AdminStats() {
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    api<AdminStats>('/api/admin/stats')
      .then(setStats)
      .catch((err: Error) => setError(err.message))
  }, [])

  if (error) return <p className="text-rose">{error}</p>
  if (!stats) return <p className="text-muted">Loading statistics…</p>

  return (
    <section>
      <p className="text-caps text-[10px] text-rose">Audience</p>
      <h2 className="mt-1 font-serif text-[32px] leading-none font-medium">Site statistics</h2>
      <p className="mt-3 max-w-[52ch] text-sm text-muted">
        First-party pageviews from the TIGG site. Bots are skipped. Countries count unique visitors, not pages.
      </p>

      <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatTile label="Today" value={stats.totals.today} />
        <StatTile label="7 days" value={stats.totals.week} />
        <StatTile label="30 days" value={stats.totals.month} />
        <StatTile label="All time" value={stats.totals.all} />
      </div>

      <div className="mt-10">
        <ViewsDayChart days={stats.days} heightClass="h-40" minBar={4} />
      </div>

      <div className="mt-12 grid gap-10 lg:grid-cols-2">
        <div>
          <h3 className="text-caps text-[11px] text-muted">Pages doing well</h3>
          <Bars rows={stats.topPages.slice(0, 5).map((row) => ({ label: row.path, views: row.views }))} />
        </div>
        <div>
          <h3 className="text-caps text-[11px] text-muted">Desktop vs mobile</h3>
          <DeviceWheel devices={stats.devices} />
        </div>
      </div>

      <div className="mt-12 grid gap-5 md:grid-cols-3">
        <div>
          <h3 className="text-caps text-[11px] text-muted">Top 5 countries</h3>
          <StatWheel
            rows={stats.countries.slice(0, 5).map((row) => ({
              label: row.country === 'unknown' ? 'Unknown' : row.country.toUpperCase(),
              views: row.views,
            }))}
          />
        </div>
        <div>
          <h3 className="text-caps text-[11px] text-muted">Referrers</h3>
          <StatWheel rows={stats.referrers.map((row) => ({ label: row.referrer, views: row.views }))} />
        </div>
        <div>
          <h3 className="text-caps text-[11px] text-muted">Browsers</h3>
          <StatWheel
            rows={(stats.browsers ?? []).map((row) => ({
              label: row.browser === 'other' ? 'Other' : row.browser.replace(/^\w/, (letter) => letter.toUpperCase()),
              views: row.views,
            }))}
          />
        </div>
      </div>
    </section>
  )
}
